/**
 * The reporting tree. Nested lists with a disclosure button on every manager:
 * Tab moves between people and buttons, Enter or Space opens and closes a
 * level, and every level collapses so the chart reads at phone width.
 *
 * Each card says its tier in words beside the key's colour (never colour
 * alone), says "Vacant position" in words, and carries a "To be confirmed"
 * marker wherever the chart's reading is inferred.
 */
import React from 'react';
import { OrgChart, OrgPosition, OrgRoster, Tier } from '../../services/orgChartApi';
import { TIER_SHORT, childrenOf, displayName, managersOf, toBeConfirmed } from './orgModel';

export function TierBadge({ tier, label }: { tier: Tier; label?: string }) {
  return (
    <span className={`oc-tier oc-tier--${tier.toLowerCase()}`}>
      <span className="oc-tier__swatch" aria-hidden="true" />
      <span className="oc-tier__text">{label || TIER_SHORT[tier]}</span>
    </span>
  );
}

export function ConfirmMarker({ text = 'To be confirmed' }: { text?: string }) {
  return (
    <span className="oc-confirm">
      <span className="oc-confirm__mark" aria-hidden="true">?</span>
      {text}
    </span>
  );
}

function rosterNote(p: OrgPosition, roster: OrgRoster | null): string | null {
  if (!roster || !roster.available || p.vacant) return null;
  const f = roster.positions[p.id];
  if (!f || !f.inRoster) return 'Not found in the eMerge roster';
  if (f.rosterActive === false) return 'Inactive in the eMerge roster';
  if (f.emailMatches === false) return 'The roster has a different email';
  return null;
}

export function PositionCard({ p, chart, roster, highlighted }: {
  p: OrgPosition; chart: OrgChart; roster: OrgRoster | null; highlighted: boolean;
}) {
  const live = roster && roster.available ? roster.positions[p.id] : undefined;
  const rosterTitle = (live && live.rosterTitle) || p.rosterTitle;
  const manager = managersOf(chart, p.id)[0];
  const note = rosterNote(p, roster);
  return (
    <article
      id={`oc-pos-${p.id}`}
      className={`oc-card oc-card--${p.tier.toLowerCase()}${p.vacant ? ' oc-card--vacant' : ''}${highlighted ? ' oc-card--hit' : ''}`}
      aria-labelledby={`oc-name-${p.id}`}
      tabIndex={-1}
    >
      <div className="oc-card__head">
        <TierBadge tier={p.tier} />
        {p.vacant && p.tier !== 'VACANT' && <span className="oc-card__flag">Vacant</span>}
        {toBeConfirmed(p) && <ConfirmMarker />}
      </div>
      <h3 className="oc-card__name" id={`oc-name-${p.id}`}>{displayName(p)}</h3>
      <p className="oc-card__title">{p.chartTitle || <em>No title on the chart</em>}</p>
      {rosterTitle && rosterTitle !== p.chartTitle && (
        <p className="oc-card__roster">Roster title: {rosterTitle}</p>
      )}
      {p.email && (
        <p className="oc-card__email">
          <a href={`mailto:${p.email}`}>{p.email}</a>
          {p.emailLooksShared && <span className="oc-card__warn"> (looks like a shared mailbox)</span>}
        </p>
      )}
      {p.vacant && manager && <p className="oc-card__cover">Until it is filled, ask {displayName(manager)}.</p>}
      {p.areas.length > 0 && (
        <ul className="oc-card__areas" aria-label={`${displayName(p)} is the contact for`}>
          {p.areas.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}
      {p.workspaces.length > 0 && (
        <ul className="oc-card__ws" aria-label="eMerge workspaces">
          {p.workspaces.map((w) => (
            <li key={`${w.workspace}-${w.as}`} className={w.inferred ? 'oc-ws oc-ws--inferred' : 'oc-ws'} title={w.basis}>
              {w.workspace.replace(/_/g, ' ').toLowerCase()}{w.inferred ? ' (inferred)' : ''}
            </li>
          ))}
        </ul>
      )}
      {note && <p className="oc-card__note">{note}</p>}
    </article>
  );
}

type TreeProps = {
  chart: OrgChart;
  roster: OrgRoster | null;
  visible: Set<string> | null;
  hits: Set<string>;
  expanded: Set<string>;
  onToggle: (id: string) => void;
};

function Node({ p, depth, kids, props }: { p: OrgPosition; depth: number; kids: Map<string | null, OrgPosition[]>; props: TreeProps }) {
  const children = (kids.get(p.id) || []).filter((c) => !props.visible || props.visible.has(c.id));
  const open = props.expanded.has(p.id);
  const listId = `oc-reports-${p.id}`;
  return (
    <li className="oc-node" data-depth={depth}>
      <PositionCard p={p} chart={props.chart} roster={props.roster} highlighted={props.hits.has(p.id)} />
      {children.length > 0 && (
        <>
          <button
            type="button"
            className="oc-toggle"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => props.onToggle(p.id)}
          >
            {open ? 'Hide' : 'Show'} {children.length} {children.length === 1 ? 'report' : 'reports'}
            <span className="oc-sr"> to {displayName(p)}</span>
          </button>
          {open && (
            <ul className="oc-level" id={listId} aria-label={`Reports to ${displayName(p)}`}>
              {children.map((c) => <Node key={c.id} p={c} depth={depth + 1} kids={kids} props={props} />)}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

export default function OrgTree(props: TreeProps) {
  const kids = childrenOf(props.chart);
  const roots = (kids.get(null) || []).filter((r) => !props.visible || props.visible.has(r.id));
  if (!roots.length) return null;
  return (
    <ul className="oc-tree" aria-label="Reporting lines">
      {roots.map((r) => <Node key={r.id} p={r} depth={0} kids={kids} props={props} />)}
    </ul>
  );
}
